<template>
  <avs-card class="position-relative">
    <!-- Overlay pendant génération de PDF -->
    <v-overlay
      :value="isGeneratingPdf"
      absolute
      style="top: 0; left: 0; right: 0; bottom: 0; z-index: 10"
    >
      <v-progress-circular
        indeterminate
        size="64"
        color="primary"
      />
    </v-overlay>
    <template #title>
      <v-toolbar
        class="mt-4 custom-toolbar"
        elevation="0"
      >
        <!-- Champ de recherche -->
        <v-text-field
          clearable
          hide-details
          prepend-inner-icon="mdi-magnify"
          :label="$t('commons.search')"
          v-model="search"
        />
        <!-- Filtre par statut -->
        <v-select
          hide-details
          dense
          outlined
          v-model="statusFilter"
          :items="statusFilterItems"
          :label="$t('commons.status.name')"
          prepend-inner-icon="mdi-filter-variant"
          style="max-width: 240px"
        />
        <!-- Fermer les anciens événements (limité à cet événement) -->
        <v-btn
          color="primary"
          outlined
          :loading="closing"
          @click="closeDialogIsOpen = true"
        >
          <v-icon left>mdi-lock-clock</v-icon>
          {{ $t("Fermer les anciens événements") }}
        </v-btn>
        <!-- Bouton Nouvelle Réservation -->
        <v-btn
          color="primary"
          @click="toNewReservation"
          class="ml-4"
        >
          <v-icon left>mdi-plus</v-icon>
          {{ $t("actions.new_female", { entity: $tc("reservation", 1) }) }}
        </v-btn>
      </v-toolbar>
    </template>
    <template #content>
      <v-data-table
        class="ma-10"
        disable-pagination
        :no-data-text="$t('messages.information.no_data')"
        :headers="headers"
        hide-default-footer
        :items="filteredDataset"
        :loading="loading"
        loading-text="Chargement des réservations..."
        no-results-text="$t('messages.information.no_data')"
        :search="search"
        @dblclick:row="toEdit"
      >
        <!-- Bouton Paiement -->
        <template #item.pay="{ item }">
          <v-tooltip bottom>
            <template #activator="{ on, attrs }">
              <v-btn
                icon
                v-bind="attrs"
                v-on="on"
                @click="openPayment(item)"
              >
                <v-icon :color="getPaymentIconColor(item)"> mdi-credit-card-outline </v-icon>
              </v-btn>
            </template>
            <span>
              {{ getPaymentTooltip(item) }}
            </span>
          </v-tooltip>
        </template>
        <!-- Colonnes standard -->
        <template #item.start_date="{ item }">{{ toDatetime(item.start_date) }}</template>
        <template #item.end_date="{ item }">{{ toDatetime(item.end_date) }}</template>
        <template #item.paid_amount="{ item }">
          <b>{{ formatPrice(item.paid_amount) }}</b>
        </template>
        <template #item.remaining_amount="{ item }">
          <b>{{ formatPrice(item.remaining_amount) }}</b>
        </template>
        <template #item.final_price="{ item }">
          <b>{{ formatPrice(item.final_price) }}</b>
        </template>
        <template #item.status="{ item }">
          <component :is="handleStatusComponent(item)" />
        </template>
        <!-- Actions : éditer, contrat, supprimer -->
        <template #item.actions="{ item }">
          <div class="d-flex justify-end">
            <!-- Éditer : désactivé si fermé -->
            <v-tooltip bottom>
              <template #activator="{ on, attrs }">
                <span
                  v-bind="attrs"
                  v-on="on"
                >
                  <avs-editButton
                    :to="getToEditLink(item)"
                    :disabled="isClosed(item)"
                  />
                </span>
              </template>
              <span v-if="isClosed(item)">
                {{ $t("Impossible de modifier une réservation fermée") }}
              </span>
            </v-tooltip>

            <!-- Générer le contrat : toujours actif -->
            <v-tooltip bottom>
              <template #activator="{ on, attrs }">
                <v-btn
                  icon
                  v-bind="attrs"
                  v-on="on"
                  @click="exportContract(item)"
                  :loading="contractLoading[item.id]"
                  :disabled="contractLoading[item.id]"
                >
                  <v-icon color="blue">mdi-file-document-outline</v-icon>
                </v-btn>
              </template>
              <span>{{ $t("Générer le contrat") }}</span>
            </v-tooltip>

            <!-- Supprimer : désactivé si fermé ou annulé -->
            <v-tooltip bottom>
              <template #activator="{ on, attrs }">
                <span
                  v-bind="attrs"
                  v-on="on"
                >
                  <avs-deleteButton
                    @click="handleOpenDeleteDialog(item)"
                    :disabled="isUndeletable(item)"
                  />
                </span>
              </template>
              <span v-if="isCancelled(item)">
                {{ $t("Impossible de supprimer une réservation annulée") }}
              </span>
              <span v-else-if="isClosed(item)">
                {{ $t("Impossible de supprimer une réservation fermée") }}
              </span>
            </v-tooltip>
          </div>
        </template>
      </v-data-table>
      <avs-deleteDialog
        v-model="deleteDialogIsOpen"
        @cancel="handleCancel"
        @delete="handleDelete"
        resource="Reservation"
      />
      <!-- Confirmation fermeture des réservations expirées -->
      <v-dialog
        v-model="closeDialogIsOpen"
        max-width="480"
      >
        <v-card>
          <v-card-title class="text-h6">
            {{ $t("Fermer les anciens événements") }}
          </v-card-title>
          <v-card-text>
            {{
              $t(
                "Toutes les réservations dont la date de fin est dépassée seront passées au statut « Fermée ». Continuer ?"
              )
            }}
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn
              text
              @click="closeDialogIsOpen = false"
              :disabled="closing"
            >
              {{ $t("commons.cancel") }}
            </v-btn>
            <v-btn
              color="primary"
              :loading="closing"
              @click="handleCloseExpired"
            >
              {{ $t("Confirmer") }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
      <!-- Modal Paiement -->
      <v-dialog
        v-model="paymentDialogOpen"
        max-width="800px"
      >
        <v-card
          class="pa-0"
          style="position: relative"
        >
          <v-card-title>
            <span class="text-h5">{{ $t("Gestion des paiements") }}</span>
          </v-card-title>
          <v-card-text>
            <div class="mb-4">
              <div class="text-h6 mb-2">
                <strong>{{ selectedReservation.name }}</strong>
              </div>
              <v-chip
                small
                :color="getStatusColor(selectedReservation.status)"
                text-color="white"
                class="mb-2"
              >
                {{ selectedReservation.status }}
              </v-chip>
              <div class="mt-2">
                <div>
                  {{ $t("Prix total") }}:
                  <b>{{ formatPrice(selectedReservation.final_price) }}</b> FCFA
                </div>
                <div>
                  {{ $t("Montant payé") }}:
                  <b class="green--text">{{ formatPrice(selectedReservation.paid_amount) }}</b> FCFA
                </div>
                <div>
                  {{ $t("Montant restant") }}:
                  <b :class="remainingAmountClass">
                    {{ formatPrice(selectedReservation.remaining_amount) }}
                  </b>
                  FCFA
                </div>
              </div>
            </div>

            <!-- Formulaire de paiement : visible si remaining_amount > 0 -->
            <div v-if="canAddPaymentToSelected">
              <v-divider class="mb-4"></v-divider>
              <div class="text-subtitle-1 mb-2">
                <strong>{{ $t("Enregistrer un nouveau paiement") }}</strong>
              </div>
              <!-- Alerte si réservation fermée mais paiement possible -->
              <v-alert
                v-if="isClosed(selectedReservation)"
                type="warning"
                dense
                text
                class="mb-3"
              >
                {{
                  $t(
                    "Cette réservation est fermée mais vous pouvez encore ajouter des paiements car il reste un montant dû."
                  )
                }}
              </v-alert>
              <v-text-field
                v-model.number="paymentAmount"
                :label="$t('Montant versé')"
                type="number"
                dense
                outlined
                class="mb-4"
                :hint="`Maximum: ${formatPrice(selectedReservation.remaining_amount)} FCFA`"
                persistent-hint
                suffix="FCFA"
              />
            </div>
            <v-alert
              v-else
              type="success"
              dense
              class="mb-4"
              text
            >
              <v-icon left>mdi-check-circle</v-icon>
              {{ $t("Réservation entièrement payée") }}
            </v-alert>

            <v-divider class="my-4" />
            <div class="text-subtitle-1 mb-3">
              <strong>{{ $t("Historique des paiements") }}</strong>
              <span class="text-caption ml-2 grey--text">
                ({{ paymentsHistory.length }} {{ $t("paiement(s)") }})
              </span>
            </div>

            <!-- Alerte si réservation fermée : modification impossible -->
            <v-alert
              v-if="isClosed(selectedReservation) && paymentsHistory.length > 0"
              type="info"
              color="secondary"
              dense
              text
              class="mb-3"
            >
              <v-icon left>mdi-lock</v-icon>
              {{ $t("Réservation fermée : les paiements existants ne peuvent pas être modifiés.") }}
            </v-alert>

            <v-data-table
              :headers="paymentHeaders"
              :items="paymentsHistory"
              dense
              hide-default-footer
              :no-data-text="$t('Aucun paiement enregistré')"
            >
              <template #item.payment_date="{ item }">
                {{ toDatetime(item.payment_date) }}
              </template>
              <template #item.amount="{ item }">
                <div v-if="editingPayment && editingPayment.id === item.id">
                  <v-text-field
                    v-model.number="editedAmount"
                    type="number"
                    dense
                    outlined
                    suffix="FCFA"
                    style="min-width: 180px"
                  />
                </div>
                <div v-else>{{ formatPrice(item.amount) }} FCFA</div>
              </template>
              <template #item.receipt_number="{ item }">
                <v-chip
                  small
                  color="primary"
                  outlined
                >
                  {{ item.receipt_number }}
                </v-chip>
              </template>
              <template #item.remaining_after="{ item }">
                <span :class="item.remaining_after === 0 ? 'green--text font-weight-bold' : ''">
                  {{ formatPrice(item.remaining_after) }} FCFA
                </span>
              </template>
              <template #item.actions="{ item }">
                <div class="d-flex align-center">
                  <!-- Actions d'édition : désactivées si fermé -->
                  <template v-if="!isClosed(selectedReservation)">
                    <div
                      v-if="editingPayment && editingPayment.id === item.id"
                      class="d-flex flex-column"
                    >
                      <v-btn
                        text
                        small
                        color="success"
                        @click="savePaymentEdit(item)"
                      >
                        <v-icon
                          small
                          left
                          >mdi-check</v-icon
                        >
                        {{ $t("commons.save") }}
                      </v-btn>
                      <v-btn
                        text
                        small
                        color="error"
                        @click="cancelPaymentEdit"
                      >
                        <v-icon
                          small
                          left
                          >mdi-close</v-icon
                        >
                        {{ $t("commons.cancel") }}
                      </v-btn>
                    </div>
                    <v-tooltip
                      bottom
                      v-else
                    >
                      <template #activator="{ on, attrs }">
                        <v-btn
                          icon
                          small
                          v-bind="attrs"
                          v-on="on"
                          @click="startPaymentEdit(item)"
                        >
                          <v-icon small>mdi-pencil</v-icon>
                        </v-btn>
                      </template>
                      <span>{{ $t("Modifier le paiement") }}</span>
                    </v-tooltip>
                  </template>

                  <!-- Exporter reçu : toujours actif -->
                  <v-tooltip bottom>
                    <template #activator="{ on, attrs }">
                      <v-btn
                        icon
                        small
                        v-bind="attrs"
                        v-on="on"
                        @click="exportReceipt(item)"
                        :loading="contractLoading[item.id]"
                        :disabled="contractLoading[item.id]"
                      >
                        <v-icon
                          small
                          color="red"
                          >mdi-file-pdf-box</v-icon
                        >
                      </v-btn>
                    </template>
                    <span>{{ $t("Télécharger le reçu") }}</span>
                  </v-tooltip>
                </div>
              </template>
            </v-data-table>
          </v-card-text>
          <v-card-actions class="justify-end pa-4">
            <v-btn
              text
              @click="paymentDialogOpen = false"
            >
              {{ $t("commons.close") }}
            </v-btn>
            <v-btn
              v-if="canAddPaymentToSelected"
              color="primary"
              @click="handlePayment"
              :disabled="!paymentAmount || paymentAmount <= 0"
            >
              <v-icon left>mdi-cash-plus</v-icon>
              {{ $t("Enregistrer le paiement") }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </template>
  </avs-card>
</template>

<script>
import { ReservationService } from "@/services"
import { toDatetime } from "@/utils/datetime"
import { Closed, Ongoing, Overdue, Pending, Cancelled } from "@/components/generics/status"
import { RESERVATION_STATUS_ITEMS } from "@/utils/consts"

export default {
  name: "ReservationsTable",
  components: { Closed, Ongoing, Overdue, Pending, Cancelled },
  data() {
    return {
      dataset: [],
      loading: false,
      search: "",
      statusFilter: "Ongoing",
      closing: false,
      closeDialogIsOpen: false,
      deleteDialogIsOpen: false,
      selectedItem: null,
      paymentDialogOpen: false,
      selectedReservation: {},
      paymentAmount: null,
      paymentsHistory: [],
      editingPayment: null,
      editedAmount: null,
      isGeneratingPdf: false,
      contractLoading: {},
      headers: [
        { text: "", value: "pay", sortable: false, width: 50 },
        { text: this.$t("Début"), value: "start_date" },
        { text: this.$t("Fin"), value: "end_date" },
        { text: this.$t("Salle"), value: "space_info.name", sortable: false },
        { text: this.$t("Client"), value: "customer_info.full_name" },
        { text: this.$t("Contact"), value: "customer_info.phone_number" },
        { text: this.$t("Status"), value: "status", sortable: false },
        { text: this.$t("Tarif"), value: "final_price", sortable: false },
        { text: this.$t("Avance"), value: "paid_amount", sortable: false },
        { text: this.$t("Restant"), value: "remaining_amount", sortable: false },
        { text: this.$t("Actions"), value: "actions", sortable: false }
      ],
      paymentHeaders: [
        { text: this.$t("Date"), value: "payment_date" },
        { text: this.$t("Montant"), value: "amount" },
        { text: this.$t("Reçu n°"), value: "receipt_number" },
        { text: this.$t("Restant après"), value: "remaining_after" },
        { text: this.$t("Actions"), value: "actions", sortable: false }
      ]
    }
  },
  computed: {
    project() {
      return this.$store.state.app.project
    },
    statusFilterItems() {
      return [
        { text: this.$t("commons.all_simple"), value: null },
        ...RESERVATION_STATUS_ITEMS.map(s => ({ text: s.text, value: s.value }))
      ]
    },
    filteredDataset() {
      if (!this.statusFilter) return this.dataset
      return this.dataset.filter(r => r.status === this.statusFilter)
    },
    canAddPaymentToSelected() {
      return this.canAddPayment(this.selectedReservation)
    },
    remainingAmountClass() {
      const remaining = this.selectedReservation.remaining_amount || 0
      if (remaining === 0) return "green--text font-weight-bold"
      if (remaining > 0) return "orange--text font-weight-bold"
      return ""
    }
  },
  methods: {
    toDatetime,
    handleStatusComponent(item) {
      return item.status
    },
    formatPrice(v) {
      return v == null
        ? ""
        : new Intl.NumberFormat("fr-FR", {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
          }).format(v)
    },
    isClosed(item) {
      return item && item.status === "Closed"
    },
    isCancelled(item) {
      return item && item.status === "Cancelled"
    },
    isUndeletable(item) {
      return this.isClosed(item) || this.isCancelled(item)
    },
    canAddPayment(item) {
      // On peut ajouter un paiement si il reste un montant à payer
      // Peu importe si la réservation est fermée ou non
      return item && item.remaining_amount > 0
    },
    getPaymentIconColor(item) {
      if (item.remaining_amount <= 0) {
        return "green"
      }
      if (this.isClosed(item)) {
        return "orange"
      }
      return "orange"
    },
    getPaymentTooltip(item) {
      if (item.remaining_amount <= 0) {
        return this.$t("Réservation entièrement payée - Consulter l'historique")
      }
      if (this.isClosed(item)) {
        return this.$t("Ajouter un paiement (réservation fermée)")
      }
      return this.$t("Enregistrer un paiement")
    },
    getStatusColor(status) {
      const colors = {
        Closed: "grey",
        Ongoing: "green",
        Pending: "orange",
        Overdue: "red"
      }
      return colors[status] || "grey"
    },
    getToEditLink(item) {
      return { name: "ReservationCreateUpdate", params: { id: item.id } }
    },
    async getDataset() {
      this.loading = true
      try {
        const resp = await ReservationService.get(this, this.project.id)
        this.dataset = resp.results
      } finally {
        this.loading = false
      }
    },
    toEdit(e, { item }) {
      // Empêcher l'édition si fermé
      if (this.isClosed(item)) {
        this.$store.commit("app/message", {
          msg: this.$t("Impossible de modifier une réservation fermée"),
          color: "warning"
        })
        return
      }
      this.$router.push(this.getToEditLink(item))
    },
    handleOpenDeleteDialog(item) {
      // Empêcher la suppression si fermé ou annulé
      if (this.isCancelled(item)) {
        this.$store.commit("app/message", {
          msg: this.$t("Impossible de supprimer une réservation annulée"),
          color: "warning"
        })
        return
      }
      if (this.isClosed(item)) {
        this.$store.commit("app/message", {
          msg: this.$t("Impossible de supprimer une réservation fermée"),
          color: "warning"
        })
        return
      }
      this.deleteDialogIsOpen = true
      this.selectedItem = item
    },
    async handleDelete() {
      await ReservationService.remove(this, this.selectedItem.id)
      this.dataset = this.dataset.filter(r => r.id !== this.selectedItem.id)
      this.$store.commit("app/message", {
        msg: this.$t("messages.success.deleted", {
          entity: this.$tc("categories.reservation.name", 1)
        }),
        color: "success"
      })
      this.deleteDialogIsOpen = false
    },
    async openPayment(item) {
      // Toujours permettre d'ouvrir la modal pour consulter l'historique
      this.selectedReservation = item
      this.paymentAmount = null
      this.editingPayment = null
      this.editedAmount = null
      this.paymentDialogOpen = true

      // Charger l'historique des paiements
      try {
        const resp = await ReservationService.getPayments(this, item.id)
        const sorted = resp.results.sort(
          (a, b) => new Date(a.payment_date) - new Date(b.payment_date)
        )
        let cumulative = 0
        this.paymentsHistory = sorted.map(p => {
          cumulative += parseFloat(p.amount)
          return { ...p, remaining_after: item.final_price - cumulative }
        })
      } catch (err) {
        this.$store.commit("app/message", {
          msg: this.$t("Erreur lors du chargement de l'historique"),
          color: "error"
        })
        this.paymentsHistory = []
      }
    },
    handleCancel() {
      this.deleteDialogIsOpen = false
    },
    async handlePayment() {
      if (!this.paymentAmount || this.paymentAmount <= 0) {
        return this.$store.commit("app/message", {
          msg: this.$t("Montant invalide"),
          color: "error"
        })
      }

      // Vérifier que le montant ne dépasse pas le restant
      if (this.paymentAmount > this.selectedReservation.remaining_amount) {
        return this.$store.commit("app/message", {
          msg: this.$t("Le montant ne peut pas dépasser le montant restant"),
          color: "error"
        })
      }

      try {
        await ReservationService.createPayment(this, this.selectedReservation.id, {
          amount: this.paymentAmount
        })
        this.$store.commit("app/message", {
          msg: this.$t("Paiement effectué avec succès"),
          color: "success"
        })

        // Recharger les données
        await this.getDataset()

        // Trouver la réservation mise à jour
        const updatedReservation = this.dataset.find(r => r.id === this.selectedReservation.id)
        if (updatedReservation) {
          await this.openPayment(updatedReservation)
        } else {
          this.paymentDialogOpen = false
        }
      } catch (err) {
        this.$store.commit("app/message", {
          msg: err?.response?.data?.amount?.[0] || this.$t("Oops, le paiement a échoué"),
          color: "error"
        })
      }
    },
    async savePaymentEdit(item) {
      if (!this.editedAmount || this.editedAmount <= 0) {
        return this.$store.commit("app/message", {
          msg: this.$t("Montant invalide"),
          color: "error"
        })
      }

      try {
        await ReservationService.updatePayment(this, this.selectedReservation.id, item.id, {
          amount: this.editedAmount
        })
        this.$store.commit("app/message", {
          msg: this.$t("Paiement modifié avec succès"),
          color: "success"
        })

        // Recharger les données
        await this.getDataset()

        // Trouver la réservation mise à jour
        const updatedReservation = this.dataset.find(r => r.id === this.selectedReservation.id)
        if (updatedReservation) {
          await this.openPayment(updatedReservation)
        }

        // Réinitialiser l'édition
        this.editingPayment = null
        this.editedAmount = null
      } catch (err) {
        this.$store.commit("app/message", {
          msg: err?.response?.data?.amount?.[0] || this.$t("Erreur lors de la modification"),
          color: "error"
        })
      }
    },
    startPaymentEdit(p) {
      this.editingPayment = p
      this.editedAmount = p.amount
    },
    cancelPaymentEdit() {
      this.editingPayment = null
      this.editedAmount = null
    },
    toNewReservation() {
      this.$router.push({ name: "ReservationCreateUpdate" })
    },
    async handleCloseExpired() {
      this.closing = true
      try {
        const { closed } = await ReservationService.closeExpired(this.project.id)
        this.$store.commit("app/message", {
          msg: this.$t("{count} réservation(s) fermée(s)", { count: closed }),
          color: "success"
        })
        await this.getDataset()
      } catch (err) {
        this.$store.commit("app/message", {
          msg: this.$t("messages.error.smtg_wrong"),
          color: "error"
        })
      } finally {
        this.closing = false
        this.closeDialogIsOpen = false
      }
    },
    async exportContract(item) {
      this.$set(this.contractLoading, item.id, true)
      this.isGeneratingPdf = true
      try {
        const blob = await ReservationService.getContractUrl(this, item.id)
        const url = URL.createObjectURL(blob)
        window.open(url)
      } catch {
        this.$store.commit("app/message", {
          msg: this.$t("messages.error.contract_generation_failed"),
          color: "error"
        })
      } finally {
        this.isGeneratingPdf = false
        this.$delete(this.contractLoading, item.id)
      }
    },
    async exportReceipt(item) {
      this.$set(this.contractLoading, item.id, true)
      this.isGeneratingPdf = true
      try {
        const blob = await ReservationService.getReceiptUrl(
          this,
          this.selectedReservation.id,
          item.id
        )
        const url = URL.createObjectURL(blob)
        window.open(url)
      } catch {
        this.$store.commit("app/message", {
          msg: this.$t("messages.error.payment_receipt_failed"),
          color: "error"
        })
      } finally {
        this.isGeneratingPdf = false
        this.$delete(this.contractLoading, item.id)
      }
    }
  },
  mounted() {
    this.getDataset()
  }
}
</script>

<style scoped>
.custom-toolbar >>> .v-toolbar__content {
  gap: 1rem;
}
</style>
